import { Injectable } from '@angular/core';
import { Color, Crop, PhotoInfoRef } from '@dreambooks/core';

/**
 * Cache service for processed photo images.
 * Caches the data URL of processed images to avoid re-loading and re-processing
 * the same images when switching between pages.
 *
 * Cache key is generated from: md5 + dimensions + rotation + color matrix
 */
@Injectable({ providedIn: 'root' })
export class PhotoBlobCacheService {
  // Map of cache key -> Promise<string> (data URL)
  // Using Promise allows concurrent requests for the same image to share a single load operation
  private cache = new Map<string, Promise<string>>();

  // Maximum cache size to prevent memory issues
  // Increased from 100 to 250 for better cache hit ratio with large albums
  private readonly maxCacheSize = 250;

  // Track access order for LRU eviction
  private accessOrder: string[] = [];

  /**
   * Generate a unique cache key for a photo with specific parameters
   */
  generateCacheKey(
    photoRef: PhotoInfoRef,
    width: number,
    height: number,
    crop?: Crop,
    color?: Color
  ): string {
    const parts: string[] = [
      photoRef.md5,
      `${Math.round(width)}x${Math.round(height)}`
    ];

    // Include crop dimensions in key — these determine which size variant
    // getNearestSize selects (e.g., _b vs _m), so different crop dimensions
    // must produce different cache keys.
    if (crop?.width && crop?.height) {
      parts.push(`cw${Math.round(crop.width)}x${Math.round(crop.height)}`);
    }

    // Include rotation in key (affects canvas drawing)
    if (crop?.rotation) {
      parts.push(`r${crop.rotation}`);
    }

    // Include fine in-container rotation (baked into the data URL too)
    const fineRotation = (crop as any)?.fineRotation;
    if (fineRotation) {
      parts.push(`fr${Math.round(fineRotation * 10) / 10}`);
    }

    // Note: crop.scale and crop.panPos are NOT included because they don't
    // affect the data URL — they're handled by SVG transforms/clip-paths.

    // Include color matrix in key (create a hash of the matrix string)
    if (color?.matrix?.length) {
      parts.push(`c${this.hashString(color.matrix)}`);
    }

    return parts.join('-');
  }

  /**
   * Get cached data URL for a photo
   * @returns Promise<string> if cached, undefined otherwise
   */
  get(key: string): Promise<string> | undefined {
    const cached = this.cache.get(key);
    if (cached) {
      // Update access order for LRU
      this.updateAccessOrder(key);
    }
    return cached;
  }

  /**
   * Check if a key exists in the cache
   */
  has(key: string): boolean {
    return this.cache.has(key);
  }

  /**
   * Set a cached data URL for a photo
   * @param key The cache key
   * @param dataUrlPromise Promise that resolves to the data URL
   */
  set(key: string, dataUrlPromise: Promise<string>): void {
    // Evict oldest entries if cache is full
    this.ensureCacheSpace();

    this.cache.set(key, dataUrlPromise);
    this.updateAccessOrder(key);

    // If the promise rejects, remove it from cache
    dataUrlPromise.catch(() => {
      this.cache.delete(key);
      this.accessOrder = this.accessOrder.filter(k => k !== key);
    });
  }

  /**
   * Clear the entire cache
   */
  clear(): void {
    this.cache.clear();
    this.accessOrder = [];
    console.log('[PhotoBlobCache] Cache cleared');
  }

  /**
   * Clear cache entries for a specific photo (all variants)
   */
  clearPhoto(md5: string): void {
    const keysToDelete: string[] = [];
    for (const key of this.cache.keys()) {
      if (key.startsWith(md5)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => {
      this.cache.delete(key);
      this.accessOrder = this.accessOrder.filter(k => k !== key);
    });
    if (keysToDelete.length > 0) {
      console.log(`[PhotoBlobCache] Cleared ${keysToDelete.length} entries for photo ${md5}`);
    }
  }

  /**
   * Get current cache size
   */
  get size(): number {
    return this.cache.size;
  }

  /**
   * Simple hash function for strings (for color matrix)
   */
  private hashString(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Update access order for LRU eviction
   */
  private updateAccessOrder(key: string): void {
    // Remove from current position
    this.accessOrder = this.accessOrder.filter(k => k !== key);
    // Add to end (most recently used)
    this.accessOrder.push(key);
  }

  /**
   * Ensure there's space in the cache by evicting oldest entries
   */
  private ensureCacheSpace(): void {
    while (this.cache.size >= this.maxCacheSize && this.accessOrder.length > 0) {
      const oldestKey = this.accessOrder.shift();
      if (oldestKey) {
        this.cache.delete(oldestKey);
        console.log(`[PhotoBlobCache] Evicted oldest entry: ${oldestKey.substring(0, 20)}...`);
      }
    }
  }
}
